/* eslint-disable react-hooks/exhaustive-deps */
import {Box, Grid} from '@amzn/awsui-components-react'
import * as queryString from 'query-string'
import React, {useEffect, useState} from 'react'
import {RouteComponentProps} from 'react-router-dom'
import {
    fetchIapsByIog,
    fetchIapsByIogl,
    fetchIogDetails,
    fetchIoglDetails,
    fetchIoglsByIog,
    queryIogsByMetadata,
} from '../../backend/backend'
import {getIogPageUrl, getMetadataSearchPageUrl} from '../../backend/internalUrls'
import {Iap, Iog, Iogl, IogMetadataResult, MetadataQuery} from '../../backend/model'
import {parseErrorMessage} from '../../utils/errors'
import {isNotEmptyQuery, isValidNumericId} from '../../validation/InputValidator'
import AjaxStatusIndicator from '../basic/AjaxStatusIndicator'
import IogSummaryPanel from './IogSummaryPanel'
import IapTable from './tables/IapTable'
import IoglTable from './tables/IoglTable'
import {MetaDataTable} from '../metadata/MetadataTable'
import {constructAdvancedSearchQuery, toAdvancedQueryString} from '../search/SearchUtils'
import IogSearchPanel from './IogSearchPanel'
import {ADVANCED_SEARCH_TAB_ID, SIMPLE_SEARCH_TAB_ID} from '../metadata/Constants'

type IogPageProps = RouteComponentProps<{ iogId: string; fc: string; view: string; }>
const BASE_PATH = '/iogs'
const MAX_RESULTS = '50'

const IogPage: React.FC<IogPageProps> = (props) => {
    const [iog, setIog] = useState<Iog>()
    const [iaps, setIaps] = useState<Iap[]>([])
    const [iogls, setIogls] = useState<Iogl[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessages, setErrorMessages] = useState<string[]>([])
    const [advancedSearchResult, setAdvancedSearchResult] = useState<IogMetadataResult[]>([])
    const [metadataNextToken, setMetadataNextToken] = useState<string | null>(null)

    const resetState = (): void => {
        setIog(undefined)
        setIogls([])
        setIaps([])
        setErrorMessages([])
        setAdvancedSearchResult([])
        setMetadataNextToken(null)
    }

    const iogIdFromUrl = props.match.params.iogId?.trim() || ''
    const queries = queryString.parse(props.location.search) || ''
    const fcIdFromUrl = (queries.fcId as string)?.trim() || ''
    const searchViewFromUrl = (queries.view as string)?.trim() || SIMPLE_SEARCH_TAB_ID

    const advancedSearchQuery = constructAdvancedSearchQuery(queries)
    const advancedSearchQueryString = toAdvancedQueryString(advancedSearchQuery)

    useEffect(() => {
        searchByIogId(iogIdFromUrl, fcIdFromUrl)
    }, [iogIdFromUrl, fcIdFromUrl, searchViewFromUrl])

    useEffect(() => {
        if (searchViewFromUrl == ADVANCED_SEARCH_TAB_ID) {
            searchByIogMetadata(advancedSearchQuery)
        }
    }, [searchViewFromUrl, advancedSearchQueryString])


    const searchByIogId = async (iogId: string, fc: string) => {
        resetState()

        if (isValidNumericId(iogId)) {
            setIsLoading(true)

            try {
                const iog = (await fetchIogDetails(iogId)).data
                setIog(iog)

                const [iogls, iaps] = await Promise.all([
                    fetchIogls(iog.iogId, fc),
                    fetchIaps(iog.iogId, fc),
                ])
                setIogls(iogls)
                setIaps(iaps)
            } catch (error: any) {
                console.error(error)
                setErrorMessages([parseErrorMessage(error)])
                setIog(undefined)
            } finally {
                setIsLoading(false)
            }
        }
    }

    const getAdvancedSearchPayload = (query: MetadataQuery, initialLoad: boolean): MetadataQuery => ({
        ...query,
        ...(!initialLoad && {nextToken: metadataNextToken}),
        maxResults: MAX_RESULTS,
    })

    const handleMetadataQuery = async (query: MetadataQuery, initialLoad: boolean) => {
        if (!isNotEmptyQuery(query)) return

        setIsLoading(true)

        try {
            const requestPayload = getAdvancedSearchPayload(query, initialLoad)
            const iogMetadataResults = (await queryIogsByMetadata(requestPayload)).data

            const displayResults: IogMetadataResult[] = iogMetadataResults.iogMetadata.map(({iogId, matchedMetadata}) =>
                matchedMetadata.reduce((result: IogMetadataResult, {key, value}) => {
                    result[key] = value
                    return result
                }, {iogId}),
            )

            if (initialLoad) {
                setAdvancedSearchResult(displayResults)
            } else {
                setAdvancedSearchResult(prevResults => [...prevResults, ...displayResults])
            }

            setMetadataNextToken(iogMetadataResults.nextToken)
        } catch (error: any) {
            console.log(error)
            setErrorMessages([parseErrorMessage(error)])
            setAdvancedSearchResult([])
        } finally {
            setIsLoading(false)
        }
    }

    const searchByIogMetadata = async (query: MetadataQuery) => {
        resetState()
        handleMetadataQuery(query, true)
    }

    const fetchIogMetadata = async (query: MetadataQuery) => {
        handleMetadataQuery(query, false)
    }

    async function fetchIogls(iogId: string, fc: string): Promise<Iogl[]> {
        if (fc) {
            const iogl = (await fetchIoglDetails(iogId, fc)).data
            return [iogl]
        } else return (await fetchIoglsByIog(iogId)).data
    }

    async function fetchIaps(iogId: string, fc: string): Promise<Iap[]> {
        if (fc) {
            return (await fetchIapsByIogl(iogId, fc)).data
        } else return (await fetchIapsByIog(iogId)).data
    }

    function getFileName(tableName: string): string {
        const fileNamePrefix = `IOG ${iogIdFromUrl}` + (fcIdFromUrl ? ` FC ${fcIdFromUrl}` : '')
        return `${fileNamePrefix} ${tableName}`
    }

    function getMetaDataFileName(tableName: string): string {
        const fileNamePrefix = `IOG ${iogIdFromUrl}` + advancedSearchQuery.toString()

        return `${fileNamePrefix} ${tableName}`
    }

    return (
        <div>
            <Box variant="h1">Inventory Owner Group (IOG)</Box>
            <IogSearchPanel iogId={iogIdFromUrl}
                            fcId={fcIdFromUrl}
                            advancedSearchQuery={advancedSearchQuery}
                            simpleSearchHandler={(iogId?: string, fc?: string) => props.history.push(getIogPageUrl(iogId, fc))}
                            advancedSearchHandler={(query: MetadataQuery) => {
                                props.history.push(getMetadataSearchPageUrl(BASE_PATH, query))
                            }}
                            initialTab={searchViewFromUrl}
            />
            <br/>
            <AjaxStatusIndicator errorMessage={errorMessages.join(' ')} loading={isLoading}/>
            <br/>
            {
                iog &&
                <IogSummaryPanel iog={iog}/>
            }
            <br/>
            {
                iog &&
                <Grid gridDefinition={[
                    {colspan: {default: 12, xxs: 5}},
                    {colspan: {default: 12, xxs: 7}},
                ]}>
                    <IapTable tableItems={iaps} fileName={getFileName('IAP Table')}/>
                    <IoglTable tableItems={iogls} fileName={getFileName('IOGL Table')}/>
                </Grid>
            }
            {
                advancedSearchResult.length > 0 &&
                <Grid gridDefinition={[{colspan: {default: 12, xxs: 12}}]}>
                    <MetaDataTable
                        metadataType={'IOG'}
                        tableItems={advancedSearchResult}
                        fileName={getMetaDataFileName('IOG Metadata table')}
                        fetchDataHandler={fetchIogMetadata}
                        advancedSearchPayload={getAdvancedSearchPayload(advancedSearchQuery, false)}
                    />
                </Grid>
            }
        </div>
    )
}

export default IogPage
